<template>
  <slot :compare-basis="compareBasis" :compare-to="compareTo" :contains-changes="containsChanges" />
</template>

<script setup lang="ts">
import { AnyObject } from '@/models/GenericInterfaces'
import { Country, Language } from '@/models/Global'
import { AdvancedProperty, Comparison, PropertyReturn } from '@/models/Property'
import { useDefaultStore } from '@/store'
import { arrayToRecord, clone } from '@/utils/tools'
import { computed, ref, watch } from 'vue'

const props = defineProps<{ property: AdvancedProperty; comparisons: Comparison[] }>()
const store = useDefaultStore()

// ----------
// -- Refs --
// ----------
const compareBasis = ref<PropertyReturn>()
const compareTo = ref<PropertyReturn>()

// --------------------
// -- Computed props --
// --------------------

// using object rather than set to easier count numbers and strings as the same
const basisOptions = computed(() =>
  compareBasis.value?.defaultOptions?.reduce((acc: Record<string | number, boolean>, o) => {
    acc[o] = true
    return acc
  }, {}),
)

const containsChanges = computed(() => {
  const valuesDiffer =
    compareBasis.value?.defaultOptions?.length !== compareTo.value?.defaultOptions?.length ||
    compareTo.value?.defaultOptions?.some((o) => {
      if (!basisOptions.value?.[o]) {
        return true
      }
    })

  const statusDiffer = compareBasis.value?.status !== compareTo.value?.status

  return valuesDiffer || statusDiffer
})

// --------------
// -- Watchers --
// --------------
watch(
  () => props.comparisons,
  () => {
    compareBasis.value = props.property.get(props.comparisons?.[0]?.product, props.comparisons?.[0]?.version?.productInput, 'enUK')
    compareTo.value = props.property.get(props.comparisons?.[1]?.product, props.comparisons?.[1]?.version?.productInput, 'enUK')
  },
  {
    immediate: true,
    deep: true,
  },
)
</script>

<style lang="scss" scoped></style>
